/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { isMobile } from 'react-device-detect'
import useRootStore from '@store/useRootStore'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
// img
import { ArrowIco } from '@shared/Icon/ArrowIcon'
import UserPickBg from '@assets/images/bg/user-pick-bg.jpg'
// css
import { TitleWrap, TotalTabWrap } from '@styles/base.style'
import { BackBtn } from '@styles/button.style'
import { HomeContentsWrap } from '@views/Home/index.style'
import NavBar from '@views/Navbar'
import { SportTab } from '@views/Matchup/Tab/SportTab'
import { LeagueTab } from '@views/Matchup/Tab/LeagueTab'
import MatchupFilterButton from '@views/Matchup/MatchUpFilter/Button'
import { PAGE_TYPE } from '@interface/common'
import { AdvButton } from '@components/Button'
import MatchupFilterPopup from '@views/Popup/MatchupFilter'
import { UserPickMatchList } from '@views/Home/UserPick/UserPickMatchList'
import { appScrollTo } from '@utils/utils'
import { UserPickWriteButton } from '@views/Home/UserPick/Components/UserPickWriteButton'
import PopupUtils from '@utils/popup-utils'
import { EXCEPTIONAL_UNIQUE_ID, FROM_VIEW_TYPE } from '@v2/constants/common'
import StorageUtils from '@utils/storage-utils'
import { UserPickMemberFilter } from '@views/Home/UserPick/Components/UserPickMemberFilter'
import { ALL_MEMBER_USER_PICK_KEY, CLASSNAMES } from '@constants/index'

let pickMatchTimer = null

const UserPickPage: React.FC = observer(() => {
  const { routerStore, userPickStore, matchupStore, logStore} = useRootStore()
  const [sportsTabClassName, setSportsTabClassName] = useState('')
  const [subTabClassName, setSubTabClassName] = useState('league-tab user-pick-tab')

  const { currentSportId, majorUniqueID } = matchupStore
  const [selectedUserPromiseIds, setSelectedUserPromiseIds] = useState<string[]>([ALL_MEMBER_USER_PICK_KEY])

  useOnMount(() => {
    // UserPickPage 가 로드되기 전 sportTab 의 useEffect 내 matchTabInitializeSportId 에 의해 majorUniqueID 값이
    // 변경 되므로 UserPickPage 가 최초 랜더링 된 시점의 majorUniqueID는 유효하지 않다.
    // onMount 가 호출되는 시점의 matchupStore의 majorUniqueID를 직접 참조 하도록 한다.
    const currentMajorUniqueId = matchupStore.majorUniqueID
    if (currentMajorUniqueId !== EXCEPTIONAL_UNIQUE_ID.NOT_STARTED_USER_PICK
      && currentMajorUniqueId !== EXCEPTIONAL_UNIQUE_ID.STARTED_USER_PICK
      && currentMajorUniqueId !== EXCEPTIONAL_UNIQUE_ID.CLOSED_USER_PICK
      && currentMajorUniqueId !== EXCEPTIONAL_UNIQUE_ID.FAVORITE_MATCH
    ) {
      matchupStore.setMajorUniqueID(EXCEPTIONAL_UNIQUE_ID.NOT_STARTED_USER_PICK)
    }
    matchupStore.setPageType(PAGE_TYPE.USER_PICK)
    StorageUtils.savePickPromotionButtonHide()
    appScrollTo(1)

    if (isMobile) {
      window.addEventListener('scroll', scrollHandler, true)
    }
  })

  useOnUnmount(() => {
    matchupStore.setPageType(undefined)
    userPickStore.resetMatches()
    if (isMobile) {
      window.removeEventListener('scroll', scrollHandler, true)
    }
  })

  useEffect(() => {
    if (matchupStore.pageType !== PAGE_TYPE.USER_PICK) {
      return
    }
    userPickStore.resetFilteredMatches()
    setSelectedUserPromiseIds([ALL_MEMBER_USER_PICK_KEY])

    const wrapElem = document.querySelector('.user-pick-user-filter-wrap > .ant-radio-group')
    if (wrapElem) {
      wrapElem.classList.add(CLASSNAMES.ADV_DISABLE_SCROLL)
    }

    clearTimeout(pickMatchTimer)
    pickMatchTimer = setTimeout(() => {
      userPickStore.fetchUserPickMatchesPackage().then(() => {
        appScrollTo(1)
        if (wrapElem) {
          wrapElem.scrollLeft = 0
          wrapElem.classList.remove(CLASSNAMES.ADV_DISABLE_SCROLL)
        }
      }).catch((err) => {
        if (wrapElem) {
          wrapElem.scrollLeft = 0
          wrapElem.classList.remove(CLASSNAMES.ADV_DISABLE_SCROLL)
        }
        console.error(err)
      })
    }, 200)
    return () => {
      clearTimeout(pickMatchTimer)
    }
  }, [currentSportId, majorUniqueID])

  const { body } = document
  const htmlElem = document.getElementsByTagName('html')[0]
  const scrollY = 200
  const scrollHandler = () => {
    if (htmlElem.scrollTop > scrollY || body.scrollTop > scrollY) {
      setSportsTabClassName('fixed-tab')
      setSubTabClassName('fixed-tab league-tab user-pick-tab')
    } else {
      setSportsTabClassName('')
      setSubTabClassName('league-tab user-pick-tab')
    }
  }

  const onClickGiveawayInfo = () => {
    logStore.writeButtonLog({ pageId: 'a040', buttonId: '002' }).catch(console.error)
    PopupUtils.showUserPickGiveawayInfo()
  }

  const userPickPageBanner = <img src={UserPickBg} alt="푸른 배경" className="pick-page-bg" loading="lazy"/>

  const onChangeMemberFilter = (selectedUserPromiseIds: string[]): void => {
    if (userPickStore.isMatchPackageLoading || userPickStore.isSpecificUsersMatchesLoading) {
      return
    }
    setSelectedUserPromiseIds(selectedUserPromiseIds)
    userPickStore.fetchUserPickSpecificUsersMatches(selectedUserPromiseIds, true).catch(console.error)
  }

  return (
    <Fragment>
      <HomeContentsWrap>
        {isMobile && (
          <TitleWrap className="title-wrap no-bg no-header">
            <BackBtn onClick={() => {
              logStore.writeButtonLog({ pageId: 'a040', buttonId: '001' }).catch(console.error)
              routerStore.goBack()
            }}>
              <ArrowIco/>
            </BackBtn>
          </TitleWrap>
        )}

        <AdvButton
          contents={userPickPageBanner}
          className={isMobile ? 'pick-bg-wrap' : 'pick-bg-wrap pc'}
          onClick={onClickGiveawayInfo}
        />

        <TotalTabWrap fixed noHeader underline noNum className={sportsTabClassName} style={{ paddingTop: 5 }}>
          <SportTab pageType={PAGE_TYPE.USER_PICK}/>
        </TotalTabWrap>

        <TotalTabWrap childFixed noHeader round className={subTabClassName} style={{ top: 45 }}>
          <LeagueTab pageType={PAGE_TYPE.USER_PICK}/>
          <MatchupFilterButton pageType={PAGE_TYPE.USER_PICK}/>
        </TotalTabWrap>

        <UserPickMemberFilter
          fromView={FROM_VIEW_TYPE.USER_PICK_MATCH_LIST}
          selectedUserPromiseIds={selectedUserPromiseIds}
          onChange={onChangeMemberFilter}
        />

        <UserPickMatchList
          className="userPickList"
          pageType={PAGE_TYPE.USER_PICK}
          userPromiseIds={selectedUserPromiseIds}
        />
      </HomeContentsWrap>

      <UserPickWriteButton fixed={isMobile} sticky={!isMobile} fromView={FROM_VIEW_TYPE.USER_PICK_MATCH_LIST}/>
      {isMobile && <NavBar key="UserPick:NavBar"/>}
      <MatchupFilterPopup/>
    </Fragment>

  )
})

export default UserPickPage
